/**
 * @module scroll-effects
 * Handles scroll-triggered behaviors using IntersectionObserver.
 * It watches elements with `data-scroll-call` attributes and fires
 * corresponding functions when they enter or leave the viewport.
 */

let inViewObserver;
let lineColorObserver;


/**
 * Updates the body's line color class.
 * Removes any existing `line-color-*` class and adds the new one.
 * @param {string} color - The color name (e.g., "blue").
 */
function setBodyLineColor(color) {
    // If a color is defined, update the body class
    if (color) {
        // Remove any existing line-color-* classes to ensure only one is active
        for (const className of document.body.classList) {
            if (className.startsWith('line-color-')) {
                document.body.classList.remove(className);
            }
        }
        // Add the new class based on the attribute value
        document.body.classList.add(`line-color-${color}`);
    }
}

/**
 * Handles changing the body's background color attribute based on section visibility.
 * When a section with `data-scroll-call="sectionBgColour"` enters the view,
 * it sets `data-bgcolor` on the `<body>` element using the value from the
 * section's `data-style-set` attribute.
 *
 * @param {string} way - 'enter' or 'exit'.
 * @param {HTMLElement} el - The section element.
 */
function sectionBgColour(way, el) {
    if (way === 'enter') {
        const styleSet = el.getAttribute('data-style-set');
        if (styleSet) {
            document.body.setAttribute('data-bgcolor', styleSet);
        }
    }
    // Note: On 'exit', we don't remove the attribute. The 'enter' event of the
    // next section will overwrite it, which provides a smooth transition.
}

/**
 * A dedicated function to handle line color changes on scroll.
 * This is called by a separate IntersectionObserver with a different threshold.
 *
 * @param {string} way - 'enter' or 'exit'.
 * @param {HTMLElement} el - The layout element.
 */
function updateLineColorOnScroll(way, el) {
    if (way === 'enter') {
        const coloredElement = el.querySelector('[data-line-color]');
        if (coloredElement) {
            const lineColor = coloredElement.getAttribute('data-line-color');
            setBodyLineColor(lineColor);
        }
    }
}

/**
 * Toggles the 'is-inview' class on elements when they enter or leave the viewport.
 * This is typically used for triggering CSS animations or transitions.
 *
 * @param {string} way - 'enter' or 'exit'.
 * @param {HTMLElement} el - The layout element (usually a <section>).
 */
function layoutInView(way, el) {
    if (way === 'enter') {
        el.classList.add('is-inview');
    } else {
        el.classList.remove('is-inview');
    }
}

/**
 * The callback for the 'is-inview' and 'sectionBgColour' observers.
 * @param {IntersectionObserverEntry[]} entries - The entries reported by the observer.
 */
function inViewCallback(entries) {
    entries.forEach(entry => {
        const way = entry.isIntersecting ? 'enter' : 'exit';
        const call = entry.target.getAttribute('data-scroll-call');

        // Dispatch based on the data-scroll-call attribute
        if (call.includes('layoutInView')) {
            layoutInView(way, entry.target);
        }
        if (call.includes('sectionBgColour')) {
            sectionBgColour(way, entry.target);
        }
    });
}

/**
 * The callback exclusively for the line color observer.
 * @param {IntersectionObserverEntry[]} entries - The entries reported by the observer.
 */
function lineColorCallback(entries) {
    entries.forEach(entry => {
        const way = entry.isIntersecting ? 'enter' : 'exit';
        updateLineColorOnScroll(way, entry.target);
    });
}

/**
 * Initializes the scroll effects by setting up the IntersectionObserver.
 * Also sets the initial line color based on the first available layout.
 * This should be called once per page load or Barba transition.
 */
export function initializeScrollEffects() {
    if (inViewObserver) inViewObserver.disconnect();
    if (lineColorObserver) lineColorObserver.disconnect();

    // --- Set initial line color on page load ---
    // Find the first layout element on the page that has a data-line-color attribute.
    // This ensures a color is set before any scrolling happens.
    const firstColoredLayout = document.querySelector('[data-barba="container"] [data-line-color]');
    if (firstColoredLayout) {
        const initialLineColor = firstColoredLayout.getAttribute('data-line-color');
        setBodyLineColor(initialLineColor);
    }

    // --- Observer for general 'is-inview' class and background colors ---
    // Triggers when 10% of the element is visible.
    const inViewOptions = {
        threshold: 0.1,
    };
    inViewObserver = new IntersectionObserver(inViewCallback, inViewOptions);

    // --- Observer for line color changes ---
    // Uses a specific rootMargin and threshold to ensure reliable triggering
    // when scrolling both up and down.
    const lineColorOptions = {
        rootMargin: '-75% 0px -25% 0px',
        threshold: 0
    };
    lineColorObserver = new IntersectionObserver(lineColorCallback, lineColorOptions);

    // Assign elements to their respective observers.
    const elementsToObserve = document.querySelectorAll('[data-scroll-call]');
    elementsToObserve.forEach(el => {
        const funcs = el.getAttribute('data-scroll-call').split(' ');

        // General observer for 'is-inview' and background colors
        if (funcs.includes('layoutInView') || funcs.includes('sectionBgColour')) {
            inViewObserver.observe(el);
        }

        // Dedicated observer for layouts that also control the line color
        if (funcs.includes('layoutInView') && el.querySelector('[data-line-color]')) {
            lineColorObserver.observe(el);
        }
    });
}

/**
 * Cleans up by disconnecting the IntersectionObserver.
 * This is crucial to call before a page transition to prevent memory leaks.
 */
export function destroyScrollEffects() {
    if (inViewObserver) {
        inViewObserver.disconnect();
        inViewObserver = null;
    }
    if (lineColorObserver) {
        lineColorObserver.disconnect();
        lineColorObserver = null;
    }
}